import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';
import { OrderedSet } from 'immutable';

export type PersistenceState = {
	recentJobId: string | null;
	isPersisted: boolean;
	date: Date | null;
	count: number;
	duration: number;
	size: number;
};

export type ExpositionsCount = {
	totalCount: number;
	availableCount: number;
};

interface IDatablockItemEntity {
	id: number;
	code: string;
	label: string;
	description: string;
	url: string;
	tags: OrderedSet<AppTagItemEntity>;
	updateDate: Date | null;
	updatedBy: string;
	creationDate: Date | null;
	createdBy: string;
	isExposed: boolean;
	isActive: boolean;
	updatedByAvatar: AvatarEntity | null;
	createdByAvatar: AvatarEntity | null;
	pictogramUrl: string;
	isActionInProgress: boolean;
	persistenceState: PersistenceState;
	expositionsCount: ExpositionsCount;
}

const entityDefaults: IDatablockItemEntity = {
	id: -1,
	label: '',
	code: '',
	description: '',
	url: '',
	tags: OrderedSet(),
	updateDate: null,
	updatedBy: '',
	creationDate: null,
	createdBy: '',
	isExposed: false,
	isActive: false,
	updatedByAvatar: null,
	createdByAvatar: null,
	pictogramUrl: '',
	isActionInProgress: false,
	persistenceState: {
		recentJobId: null,
		isPersisted: false,
		count: 0,
		duration: 0,
		size: 0,
		date: null,
	},
	expositionsCount: {
		totalCount: 0,
		availableCount: 0,
	},
};

export class DatablockItemEntity implements IDatablockItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public code = entityDefaults.code;
	public description = entityDefaults.description;
	public url = entityDefaults.url;
	public label = entityDefaults.label;
	public tags = entityDefaults.tags;
	public updateDate = entityDefaults.updateDate;
	public updatedBy = entityDefaults.updatedBy;
	public creationDate = entityDefaults.creationDate;
	public createdBy = entityDefaults.createdBy;
	public isExposed = entityDefaults.isExposed;
	public isActive = entityDefaults.isActive;
	public updatedByAvatar = entityDefaults.updatedByAvatar;
	public createdByAvatar = entityDefaults.createdByAvatar;
	public pictogramUrl = entityDefaults.pictogramUrl;
	public isActionInProgress = entityDefaults.isActionInProgress;
	public persistenceState: PersistenceState = {
		recentJobId: entityDefaults.persistenceState.recentJobId,
		count: entityDefaults.persistenceState.count,
		date: entityDefaults.persistenceState.date,
		duration: entityDefaults.persistenceState.duration,
		isPersisted: entityDefaults.persistenceState.isPersisted,
		size: entityDefaults.persistenceState.size,
	};
	public expositionsCount: ExpositionsCount = {
		totalCount: entityDefaults.expositionsCount.totalCount,
		availableCount: entityDefaults.expositionsCount.availableCount,
	};
	public static build(
		params: Partial<IDatablockItemEntity> = {}
	): DatablockItemEntity {
		const entity = new DatablockItemEntity();
		entity.id = params.id ?? entityDefaults.id;
		entity.code = params.code ?? entityDefaults.code;
		entity.description = params.description ?? entityDefaults.description;
		entity.label = params.label ?? entityDefaults.label;
		entity.url = params.url ?? entityDefaults.url;
		entity.tags = params.tags ?? entityDefaults.tags;
		entity.updateDate = params.updateDate ?? entityDefaults.updateDate;
		entity.updatedBy = params.updatedBy ?? entityDefaults.updatedBy;
		entity.creationDate = params.creationDate ?? entityDefaults.creationDate;
		entity.createdBy = params.createdBy ?? entityDefaults.createdBy;
		entity.isExposed = params.isExposed ?? entityDefaults.isExposed;
		entity.isActive = params.isActive ?? entityDefaults.isActive;
		entity.createdByAvatar =
			params.createdByAvatar ?? entityDefaults.createdByAvatar;
		entity.pictogramUrl = params.pictogramUrl ?? entityDefaults.pictogramUrl;
		entity.persistenceState =
			params.persistenceState ?? entity.persistenceState;
		entity.isActionInProgress =
			params.isActionInProgress ?? entity.isActionInProgress;
		if (params.updatedBy) {
			entity.updatedByAvatar = AvatarEntity.build(params.updatedBy);
		}
		if (params.createdBy) {
			entity.createdByAvatar = AvatarEntity.build(params.createdBy);
		}
		entity.expositionsCount =
			params.expositionsCount ?? entityDefaults.expositionsCount;
		return entity;
	}

	public setJobInProgress(value: boolean): DatablockItemEntity {
		return produce(this, (draft: DatablockItemEntity) => {
			draft.isActionInProgress = value;
		});
	}

	public setPersistenceInfo(info: {
		isPersisted: boolean;
		partitionCount: number;
		persistenceDate: Date | null;
		persistenceDuration: number;
		persistenceSize: number;
	}): DatablockItemEntity {
		return produce(this, (draft: DatablockItemEntity) => {
			draft.persistenceState.isPersisted = info.isPersisted;
			draft.persistenceState.count = info.partitionCount;
			draft.persistenceState.date = info.persistenceDate;
			draft.persistenceState.duration = info.persistenceDuration;
			draft.persistenceState.size = info.persistenceSize;
			draft.isActionInProgress = false;
		});
	}

	public updateDatablockExpositionsCount(
		expositionsCount: ExpositionsCount
	): DatablockItemEntity {
		return produce(this, (draft: DatablockItemEntity) => {
			draft.expositionsCount.totalCount = expositionsCount.totalCount;
			draft.expositionsCount.availableCount = expositionsCount.availableCount;
		});
	}

	public setJobId(value: string | null): DatablockItemEntity {
		return produce(this, (draft: DatablockItemEntity) => {
			draft.persistenceState = {
				...draft.persistenceState,
				recentJobId: value,
			};
		});
	}

	public setOnGoingPersistence(): DatablockItemEntity {
		return produce(this, (draft) => {
			draft.isActionInProgress = true;
		});
	}
}
